import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supplierAPI, supplierInvoicesAPI, posAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/useIsMobile';
import { Truck, CheckCircle2, Package, ShoppingCart, History, Pencil, RotateCcw, X } from 'lucide-react';
import ReceiptModal from '@/components/ReceiptModal';
import InvoiceReceiptModal from '@/components/InvoiceReceiptModal';

function formatDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
}

function formatCurrency(n) {
  return `₹${parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const IN_TRANSIT_STATUSES = new Set(['pending', 'pending_revised', 'supplier_responded', 'disputed']);

function grnRowClass(status) {
  if (status === 'acknowledged') return 'border-l-4 border-green-400 bg-green-50 dark:bg-green-900/10';
  if (IN_TRANSIT_STATUSES.has(status)) return 'border-l-4 border-yellow-400 bg-yellow-50 dark:bg-yellow-900/10';
  if (status === 'cancelled') return 'border-l-4 border-gray-400 bg-gray-50 dark:bg-gray-800/50';
  return '';
}

const STATUS_LABEL = {
  pending:            'In Transit',
  pending_revised:    'In Transit (Updated)',
  supplier_responded: 'In Transit',
  disputed:           'In Transit',
  acknowledged:       'Delivered (GRN)',
  cancelled:          'Cancelled',
  rejected:           'Rejected',
};

function StatusPill({ status, cancelReason }) {
  const label = status === 'cancelled' && cancelReason === 'sale_deleted'
    ? 'Cancelled (Deleted from Sales History)'
    : STATUS_LABEL[status] || status;
  if (status === 'acknowledged') {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 font-medium">
        <CheckCircle2 className="w-3 h-3" />
        {label}
      </span>
    );
  }
  if (IN_TRANSIT_STATUSES.has(status)) {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 font-medium">
        <Truck className="w-3 h-3" />
        {label}
      </span>
    );
  }
  if (status === 'cancelled') {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300 font-medium">
        <X className="w-3 h-3" />
        {label}
      </span>
    );
  }
  if (status === 'rejected') {
    return <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">{label}</span>;
  }
  return <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{label}</span>;
}

// ─────────────────────────────────────────────────────────────────────────────

const FILTER_ALL        = 'all';
const FILTER_IN_TRANSIT = 'in_transit';
const FILTER_DELIVERED  = 'delivered';

export default function SupplierInvoiceHistory() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const { storeProfile } = useAuth();
  const [filter, setFilter] = useState(FILTER_ALL);
  const [loadingId, setLoadingId] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null); // invoice object awaiting confirm
  const [receiptSale, setReceiptSale]       = useState(null);
  const [receiptInvoice, setReceiptInvoice] = useState(null);

  const apiStatus = filter === FILTER_IN_TRANSIT
    ? 'pending,pending_revised,supplier_responded,disputed'
    : filter === FILTER_DELIVERED
    ? 'acknowledged'
    : undefined;

  const { data, isLoading, error } = useQuery({
    queryKey: ['supplier-invoices', apiStatus],
    queryFn: () => supplierAPI.getInvoices(apiStatus ? { status: apiStatus } : {}),
    select: r => r.data,
    staleTime: 60_000,
  });

  const invoices = data?.invoices ?? [];
  const total    = data?.total    ?? 0;
  const inTransitCount = invoices.filter(i => IN_TRANSIT_STATUSES.has(i.status)).length;
  const deliveredCount = invoices.filter(i => i.status === 'acknowledged').length;

  const handleEditInvoice = useCallback(async (inv) => {
    if (!inv.pos_bill_number) {
      toast.error('No bill number linked to this invoice');
      return;
    }
    setLoadingId(inv.id);
    try {
      const res = await posAPI.getSale(inv.pos_bill_number);
      navigate('/supplier/billing', { state: { amendSale: res.data } });
    } catch {
      toast.error('Could not load bill for editing');
    } finally {
      setLoadingId(null);
    }
  }, [navigate]);

  const handleRestoreInvoice = useCallback(async (inv) => {
    setLoadingId(inv.id);
    try {
      await supplierInvoicesAPI.restore(inv.id);
      toast.success('Invoice restored — retailer will see it as In Transit again.');
      qc.invalidateQueries({ queryKey: ['supplier-invoices'] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to restore invoice');
    } finally {
      setLoadingId(null);
    }
  }, [qc]);

  const handleCancelInvoice = useCallback(async () => {
    if (!cancelTarget) return;
    const inv = cancelTarget;
    setCancelTarget(null);
    setLoadingId(inv.id);
    try {
      await supplierInvoicesAPI.cancel(inv.id);
      toast.success('Invoice cancelled.');
      qc.invalidateQueries({ queryKey: ['supplier-invoices'] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to cancel invoice');
    } finally {
      setLoadingId(null);
    }
  }, [cancelTarget, qc]);

  const handleViewTile = useCallback(async (inv) => {
    try {
      if (inv.pos_bill_number) {
        const res = await posAPI.getSale(inv.pos_bill_number);
        setReceiptSale(res.data);
      } else {
        const res = await supplierAPI.getInvoice(inv.id);
        setReceiptInvoice(res.data);
      }
    } catch {
      toast.error('Could not load receipt');
    }
  }, []);

  return (
    <div className="h-full flex flex-col min-h-0 bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700">
        {/* Top row — page nav toggle */}
        <div className="flex items-center justify-between px-4 py-3">
          <h2 className="text-base font-semibold text-foreground">Sent Invoices</h2>
          {isMobile ? (
            <div className="inline-flex items-center gap-0.5 bg-muted rounded-lg p-0.5">
              <button
                onClick={() => navigate('/supplier/billing')}
                className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all text-muted-foreground hover:text-foreground whitespace-nowrap"
              >
                <ShoppingCart className="w-3 h-3 shrink-0" />
                POS
              </button>
              <button
                onClick={() => navigate('/supplier/billing?view=history')}
                className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all text-muted-foreground hover:text-foreground whitespace-nowrap"
              >
                <History className="w-3 h-3 shrink-0" />
                Sales
              </button>
              <button className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all bg-background text-foreground shadow-sm whitespace-nowrap">
                <Truck className="w-3 h-3 shrink-0" />
                Invoices
              </button>
            </div>
          ) : (
            <div className="inline-flex items-center gap-1 bg-muted rounded-lg p-1">
              <button
                onClick={() => navigate('/supplier/billing')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all text-muted-foreground hover:text-foreground whitespace-nowrap"
              >
                <ShoppingCart className="w-3.5 h-3.5 shrink-0" />
                POS
              </button>
              <button
                onClick={() => navigate('/supplier/billing?view=history')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all text-muted-foreground hover:text-foreground whitespace-nowrap"
              >
                <History className="w-3.5 h-3.5 shrink-0" />
                Sales History
              </button>
              <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all bg-background text-foreground shadow-sm whitespace-nowrap">
                <Truck className="w-3.5 h-3.5 shrink-0" />
                Sent Invoices
              </button>
            </div>
          )}
        </div>

        {/* Second row — filter + count */}
        <div className="flex items-center justify-between px-4 pb-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">{total} invoice{total !== 1 ? 's' : ''}</p>
          <div className="inline-flex items-center gap-0.5 bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
            {[
              { id: FILTER_ALL,        label: 'All' },
              { id: FILTER_IN_TRANSIT, label: 'In Transit', icon: <Truck className="w-3 h-3" /> },
              { id: FILTER_DELIVERED,  label: 'Delivered',  icon: <CheckCircle2 className="w-3 h-3" /> },
            ].map(({ id, label, icon }) => (
              <button
                key={id}
                onClick={() => setFilter(id)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all whitespace-nowrap ${
                  filter === id
                    ? id === FILTER_IN_TRANSIT
                      ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400 shadow-sm'
                      : id === FILTER_DELIVERED
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400 shadow-sm'
                      : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                {icon}
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Summary strip */}
      {filter === FILTER_ALL && (inTransitCount > 0 || deliveredCount > 0) && (
        <div className="flex gap-3 px-4 py-2 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
            <Truck className="w-3 h-3" />
            {inTransitCount} in transit
          </span>
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
            <CheckCircle2 className="w-3 h-3" />
            {deliveredCount} delivered
          </span>
        </div>
      )}

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="flex items-center justify-center h-full">
            <div className="text-gray-400 text-sm">Loading invoices…</div>
          </div>
        )}
        {error && (
          <div className="flex items-center justify-center h-full">
            <div className="text-red-500 text-sm">Failed to load invoices.</div>
          </div>
        )}
        {!isLoading && !error && invoices.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full py-20 text-center px-6">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
              <Package className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-600 dark:text-gray-400 font-medium">
              {filter === FILTER_IN_TRANSIT ? 'No in-transit invoices' :
               filter === FILTER_DELIVERED  ? 'No delivered invoices'  :
               'No invoices sent yet'}
            </p>
            <p className="text-gray-400 dark:text-gray-600 text-sm mt-1">
              Invoices appear here when you bill a connected retailer.
            </p>
          </div>
        )}
        {!isLoading && !error && invoices.length > 0 && (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {invoices.map(inv => {
              const canEdit    = IN_TRANSIT_STATUSES.has(inv.status);
              const canCancel  = IN_TRANSIT_STATUSES.has(inv.status);
              const canRestore = inv.status === 'cancelled' && inv.cancel_reason !== 'sale_deleted';
              const isLoading  = loadingId === inv.id;
              return (
                <div key={inv.id} className={`px-4 py-3.5 cursor-pointer ${grnRowClass(inv.status)}`} onClick={() => handleViewTile(inv)}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
                          {inv.shop_name || inv.store_id}
                        </span>
                        {inv.pos_bill_number && (
                          <span className="text-xs text-gray-400 dark:text-gray-500">#{inv.pos_bill_number}</span>
                        )}
                      </div>
                      <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                        <StatusPill status={inv.status} cancelReason={inv.cancel_reason} />
                        {canEdit && (
                          <button
                            onClick={e => { e.stopPropagation(); handleEditInvoice(inv); }}
                            disabled={isLoading}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/40 font-medium transition-colors disabled:opacity-50"
                          >
                            <Pencil className="w-3 h-3" />
                            {isLoading ? 'Loading…' : 'Edit'}
                          </button>
                        )}
                        {canCancel && (
                          <button
                            onClick={e => { e.stopPropagation(); setCancelTarget(inv); }}
                            disabled={isLoading}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/40 font-medium transition-colors disabled:opacity-50"
                          >
                            <X className="w-3 h-3" />
                            Cancel
                          </button>
                        )}
                        {canRestore && (
                          <button
                            onClick={e => { e.stopPropagation(); handleRestoreInvoice(inv); }}
                            disabled={isLoading}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400 dark:hover:bg-emerald-900/40 font-medium transition-colors disabled:opacity-50"
                          >
                            <RotateCcw className="w-3 h-3" />
                            {isLoading ? 'Restoring…' : 'Restore'}
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        {formatDate(inv.invoice_date)} · {inv.payment_mode}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(inv.total_amount)}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{formatDate(inv.created_at)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {receiptSale && (
        <ReceiptModal sale={receiptSale} storeProfile={storeProfile} onClose={() => setReceiptSale(null)} />
      )}
      {receiptInvoice && (
        <InvoiceReceiptModal invoice={receiptInvoice} onClose={() => setReceiptInvoice(null)} />
      )}

      {/* Cancel confirmation modal */}
      {cancelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-sm p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">Cancel this invoice?</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              Cancelling only removes the invoice from the retailer's view — it does <strong>not</strong> delete the corresponding bill from your Sales History.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500 mb-5">
              To delete the bill itself, go to the <strong>Sales History</strong> page and delete it from there.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setCancelTarget(null)}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Go back
              </button>
              <button
                onClick={handleCancelInvoice}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors"
              >
                Yes, cancel invoice
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
